import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import routerFunctions from "@/router/routerFunctions";

import { registerGuard } from "./Guard";

import StartpageView from "@/views/startpage/StartpageView.vue";
import EmptyRouterView from "../views/EmptyRouterView.vue";
import NotFound from "../components/shared/NotFound.vue";
import DashboardView from "../views/dashboard/DashboardView.vue";
import KioskView from "@/views/kiosk/KioskView.vue";
import ProfileView from "../views/profile/ProfileView.vue";
import CompanyView from "../views/company/CompanyView.vue";
import ProjectsView from "../views/projects/ProjectsView.vue";
import ProjectView from "../views/projects/project/ProjectView.vue";
import DocumentsView from "../views/documents/DocumentsView.vue";
import HmsView from "../views/projects/project/hms/HmsView.vue";
import ComplaintsView from "../views/complaints/ComplaintsView.vue";
import ComplaintView from "../views/complaints/ComplaintView.vue";
import AberrationView from "../views/aberration/AberrationView.vue";
import RUHView from "../views/ruh/RUHView.vue";
import ROView from "../views/ro/ROView.vue";
import MeasuresView from "../views/measures/MeasuresView.vue";
import MeasuresDetailView from "../views/measures/MeasuresDetailView.vue";
import CheckupsView from "../views/checkups/CheckupsView.vue";
import CheckupView from "../views/checkups/CheckupView.vue";
import ChecklistsView from "@/views/checklists/ChecklistsView.vue";
import ChecklistView from "@/views/checklists/ChecklistView.vue";
import MinutesView from "@/views/projects/project/minutes/MinutesView.vue";
import BlueprintsView from "@/views/projects/project/blueprints/BlueprintsView.vue";
import BuildingBlueprintsView from "@/views/projects/project/building/blueprints/BuildingBlueprintsView.vue";
import ContactsView from "@/views/projects/project/contacts/ContactsView.vue";
import OrdersView from "@/views/projects/project/house/orders/OrdersView.vue";
import OrderView from "@/views/projects/project/house/orders/order/OrderView.vue";
import ProjectBookings from "@/views/projects/project/bookings/BookingsView.vue";
import ProjectBooking from "@/views/projects/project/bookings/booking/BookingView.vue";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardView,
    meta: {
      headerName: "Dashboard",
    },
  },
  {
    path: "/kiosk",
    name: "Kiosk",
    component: KioskView,
    meta: {
      notRequiresAuth: true,
      hideNavigation: true,
    },
  },
  {
    path: "/",
    name: "Startpage",
    component: StartpageView,
    meta: {
      notRequiresAuth: true,
      hideNavigation: true,
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: ProfileView,
    meta: {
      headerName: "Min profil",
    },
  },
  {
    path: "/company",
    name: "Company",
    component: CompanyView,
    meta: {
      headerName: "Mitt firma",
    },
    beforeEnter: routerFunctions.checkCompanyAccess,
  },

  {
    path: "/projects",
    component: EmptyRouterView,
    children: [
      {
        path: "",
        name: "Projects",
        component: ProjectsView,
        meta: {
          headerName: "Prosjekter",
        },
      },
      {
        path: ":id",
        name: "Project",
        component: ProjectView,
      },
      {
        path: ":id/actions",
        name: "ProjectActions",
        component: MeasuresView,
        meta: {
          headerName: "Tiltak Prosjekt",
        },
      },
      {
        path: ":id/house/:houseId",
        name: "ProjectHouse",
        component: ProjectView,
      },
      {
        path: ":id/house/:houseId/orders",
        name: "OrdersView",
        component: OrdersView,
        meta: {
          headerName: "Bestillinger",
          tableBtn: true,
        },
      },
      {
        path: ":id/house/:houseId/orders/:orderId",
        name: "OrderView",
        component: OrderView,
        meta: {
          headerName: "Bestilling",
        },
      },

      {
        path: ":id/documents",
        name: "ProjectDocuments",
        component: DocumentsView,
        meta: {
          headerName: "Dokumenter",
        },
      },
      {
        path: ":id/documents/:uuid",
        name: "ProjectDocumentsWithUuid",
        component: DocumentsView,
        meta: {
          headerName: "Dokumenter",
        },
      },
      {
        path: ":id/hms",
        name: "ProjectHms",
        component: HmsView,
        meta: {
          headerName: "HMS plan",
        },
      },
      {
        path: ":id/hms/:uuid",
        name: "ProjectHmsWithUuid",
        component: HmsView,
        meta: {
          headerName: "HMS plan",
        },
      },
      {
        path: ":id/minutes",
        name: "ProjectMinutes",
        component: MinutesView,
        meta: {
          headerName: "Møtereferater",
        },
      },
      {
        path: ":id/blueprints",
        name: "ProjectBlueprints",
        component: BlueprintsView,
        meta: {
          headerName: "Tegninger",
        },
      },
      {
        path: ":id/blueprints/:uuid",
        name: "ProjectBlueprintsUuid",
        component: BlueprintsView,
        meta: {
          headerName: "Tegninger",
        },
      },
      {
        path: ":id/contacts",
        name: "ProjectContacts",
        component: ContactsView,
        meta: {
          headerName: "Kontakter",
        },
      },
      {
        path: ":id/bookings",
        name: "ProjectBookings",
        component: ProjectBookings,
        meta: {
          headerName: "Bookinger",
        },
      },
      {
        path: ":id/bookings/booking",
        name: "ProjectBooking",
        component: ProjectBooking,
        meta: {
          headerName: "Booking",
        },
      },

      {
        path: ":id/house/:houseId/blueprints",
        name: "BuildingBlueprintsView",
        component: BuildingBlueprintsView,
        meta: {
          headerName: "Byggtegninger",
        },
      },
      {
        path: ":id/house/:houseId/blueprints/:uuid",
        name: "BuildingBlueprintsViewUuid",
        component: BuildingBlueprintsView,
        meta: {
          headerName: "Byggtegninger",
        },
      },
    ],
  },
  {
    path: "/documents",
    name: "Documents",
    component: DocumentsView,
    meta: {
      headerName: "Dokumenter",
    },
  },

  {
    path: "/complaints",
    component: EmptyRouterView,
    children: [
      {
        path: "",
        name: "Complaints",
        component: ComplaintsView,
        meta: {
          headerName: "Reklamasjoner",
          tableBtn: true,
        },
      },
      {
        path: ":id",
        name: "Complaint",
        component: ComplaintView,
        meta: {
          headerName: "Reklamasjon",
        },
      },
    ],
  },

  {
    path: "/request",
    component: EmptyRouterView,
    children: [
      {
        path: "",
        name: "Requests",
        component: ComplaintsView,
        meta: {
          headerName: "Henvendelser",
          tableBtn: true,
        },
      },
      {
        path: ":id",
        name: "Request",
        component: ComplaintView,
        meta: {
          headerName: "Henvendelse",
        },
      },
    ],
  },
  {
    path: "/aberration",
    name: "Aberration",
    component: AberrationView,
    meta: {
      headerName: "Avvik",
    },
  },
  {
    path: "/ruh",
    name: "RUH",
    component: RUHView,
    meta: {
      headerName: "Ny HMS-H",
    },
  },
  {
    path: "/ro",
    name: "RO",
    component: ROView,
    meta: {
      headerName: "Ny HMS-O",
    },
  },

  {
    path: "/actions",
    component: EmptyRouterView,
    children: [
      {
        path: "",
        name: "Actions",
        component: MeasuresView,
        meta: {
          headerName: "Tiltak",
          tableBtn: true,
        },
      },
      // {
      //   path: "new",
      //   name: "NewMeasure",
      //   component: NewRequest,
      //   meta: { mobName: "Ny henvendelse" }
      // },
      {
        path: ":id",
        name: "Action",
        component: MeasuresDetailView,
        meta: {
          headerName: "Tiltak",
        },
      },
    ],
  },
  {
    path: "/checkups",
    component: EmptyRouterView,
    children: [
      {
        path: "",
        name: "Checkups",
        component: CheckupsView,
        meta: {
          headerName: "Egenkontroller",
          tableBtn: true,
        },
      },
      {
        path: ":id",
        name: "Checkup",
        component: CheckupView,
        meta: {
          headerName: "Egenkontroll",
        },
      },
    ],
  },

  {
    path: "/checklist",
    component: EmptyRouterView,
    children: [
      {
        path: "",
        name: "Checklists",
        component: ChecklistsView,
        meta: {
          headerName: "Sjekklister",
          onlyTrysilhus: true,
          tableBtn: true,
        },
      },
      {
        path: ":id",
        name: "checklist",
        component: ChecklistView,
        meta: {
          headerName: "Sjekkliste",
        },
      },
      {
        path: "/404",
        name: "404",
        component: NotFound,
        meta: {
          hideNavigation: true,
        },
      },
      {
        path: "/:pathMatch(.*)*",
        redirect: "/404",
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

const originalPush = router.push.bind(router);
router.push = function push(location) {
  return originalPush(location).catch((err) => {
    if (VueRouter.isNavigationFailure(err, VueRouter.NavigationFailureType.duplicated)) {
      return err;
    }
    throw err;
  });
} as typeof router.push;

const originalReplace = router.replace.bind(router);
router.replace = function replace(location) {
  return originalReplace(location).catch((err) => {
    if (VueRouter.isNavigationFailure(err, VueRouter.NavigationFailureType.duplicated)) {
      return err;
    }
    throw err;
  });
} as typeof router.replace;

registerGuard(router);

// router.beforeEach(async (to, from, next) => {
//   const vm = router.app as any;

//   if (to.meta?.notRequiresAuth) {
//     const test = await localStorage.getItem("btLogin");
//     if (test) {
//       router.push(test);
//       await localStorage.setItem("btLogin", "");
//     } else {
//       // if (to.name === "Startpage") {
//       //   const isAuthenticated = await vm.$msal.isAuthenticated();
//       //   if (isAuthenticated) {
//       //     router.push({ name: "Dashboard" });
//       //   }
//       // }
//       next();
//     }
//   } else {
//     const isAuthenticated = await vm.$msal.isAuthenticated();
//     if (isAuthenticated) {
//       console.info("$msal", vm.$msal);
//     }

//     if (!isAuthenticated) {
//       if (to.name === "Kiosk") {
//         next();
//       } else if (from.name === "Startpage") {
//         await vm.$msal.signIn();
//       } else {
//         router.push({ name: "Startpage" });
//       }
//     } else {
//       if (isAuthenticated && store.getters["shared/profile"] === null) {
//         await store.dispatch("shared/setProfile");
//       }

//       // const isTrysilhus = await store.getters["shared/isTrysilhus"];
//       //Route is only for trysilhus
//       // if (to.meta?.onlyTrysilhus && !isTrysilhus) {
//       //   notifications.warn(`Du har ikke tilgang til ${to?.meta?.headerName}`);
//       //   router.push({ name: "Dashboard" });
//       // }
//       next();
//     }
//   }
// });

// router.beforeEach(async (to, from, next) => {
//   // const vm = router.app as any;

//   // console.info("vm.$msal", vm.$msal);
//   debugger;
//   console.info("to", to);
//   console.info("from", from);
//   // if (to.hash && vm.$msal.data.custom.redirectPath) {
//   //   debugger;
//   //   // router.push(to.path);
//   //   // router.push({ name: to?.name });
//   //   const redirectPath = JSON.parse(
//   //     JSON.stringify(vm.$msal.data.custom.redirectPath)
//   //   );
//   //   router.push(redirectPath);
//   //   // next();
//   // }

//   if (to.meta?.notRequiresAuth) {
//     debugger;
//     // const test = localStorage.getItem("btLogin");
//     const test = await JSON.parse((localStorage as any).getItem("btLogin"));
//     console.info("test", test);
//     if (to.name === "Startpage" && !to.hash && test) {
//       debugger;
//       router.push({ name: test.name });
//       // localStorage.setItem("btLogin", "hmm");

//       // next();
//     } else {
//       debugger;
//       next();
//     }
//     // debugger;
//     // console.log(vm.$msal.data.custom.test123);
//     // if (to.name === "Startpage" && vm.$msal.data.custom.test123) {
//     //   debugger;
//     //   router.push({ name: "Checklists" });
//     // } else {
//     // next();
//     // }
//     // if (to.name === "Startpage" && vm.$msal.data.custom.redirectPath) {
//     //   debugger;
//     //   const redirectPath = JSON.parse(
//     //     JSON.stringify(vm.$msal.data.custom.redirectPath)
//     //   );

//     //   console.info("redirectPath123", redirectPath);
//     //   router.push(redirectPath);
//     //   //   // vm.$msal.saveCustomData("redirectPath", "");
//     //   // next();
//     // } else {

//     // }
//   } else {
//     const vm = router.app as any;
//     const isAuthenticated = await vm.$msal.isAuthenticated();

//     if (!isAuthenticated) {
//       debugger;
//       if (from.name === "Startpage") {
//         localStorage.setItem("btLogin", JSON.stringify(to));
//         debugger;
//         await vm.$msal.signIn();
//         // next();
//       } else {
//         debugger;
//         router.push({ name: "Startpage" });
//       }
//       // console.info("!isAuthenticated");

//       // next();
//     } else {
//       if (isAuthenticated && store.getters["shared/profile"] === null) {
//         await store.dispatch("shared/setProfile");
//       }

//       const isTrysilhus = await store.getters["shared/isTrysilhus"];

//       // if (to.hash && to.name) {
//       //   debugger;
//       //   // router.push(to.path);
//       //   router.push({ name: to?.name });
//       //   next();
//       // }
//       //Route is only for trysilhus
//       if (to.meta?.onlyTrysilhus && !isTrysilhus) {
//         notifications.warn(`Du har ikke tilgang til ${to?.meta?.headerName}`);
//         router.push({ name: "Dashboard" });
//       }
//       next();
//     }
//   }
// });

export default router;
